import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAppState } from 'src/state-management/state-provider';
import {
  Site,
  LocationAdminDetail,
  DeploymentAdminDetailBase,
} from 'src/api/Api';
import DeploymentTimeline from './DeploymentTimeline';

interface FilterPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  onDataPanelOpen: () => void;
  onAnalyticsPanelOpen: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  onToggle,
  onDataPanelOpen,
  onAnalyticsPanelOpen,
}) => {
  const panelWidth = 360;
  const collapsedWidth = 56;
  const { state, dispatch } = useAppState();
  const { bioMapData, projectDetails } = state.selectedProject;
  const { selectedSite, selectedLocation, selectedDeployment } =
    state.bioInteractiveMapFilter;

  const selectedSiteId = selectedSite?.site_id ?? 'all';
  const selectedLocationId = selectedLocation?.location_id ?? 'all';
  const [filteredLocations, setFilteredLocations] = useState<
    LocationAdminDetail[]
  >([]);

  useEffect(() => {
    if (bioMapData?.locations) {
      if (selectedSiteId === 'all') {
        setFilteredLocations(bioMapData.locations);
      } else {
        const locations = bioMapData.locations.filter(
          (location: LocationAdminDetail) => location.site_id === selectedSiteId
        );
        setFilteredLocations(locations);
      }
    } else {
      setFilteredLocations([]);
    }
  }, [selectedSiteId, bioMapData?.locations]);

  const locationDeployments = useMemo(() => {
    if (!selectedLocation || !bioMapData?.deployments) {
      return [];
    }
    return bioMapData.deployments
      .filter((d) => d.location_id === selectedLocation.location_id)
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  }, [selectedLocation, bioMapData?.deployments]);

  const handleDeploymentSelect = (deployment?: DeploymentAdminDetailBase) => {
    const device = deployment
      ? bioMapData?.devices?.find((d) => d.device_id === deployment.device_id)
      : undefined;

    dispatch({
      type: 'SET_BIO_INTERACTIVE_MAP_FILTER',
      payload: {
        ...state.bioInteractiveMapFilter,
        selectedDeployment: deployment,
        selectedDevice: device,
      },
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 1200,
        width: isOpen ? panelWidth : collapsedWidth,
        height: isOpen ? '94%' : collapsedWidth,
        maxHeight: 'calc(94% - 32px)',
        borderRadius: isOpen ? 4 : '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create(['width', 'height', 'border-radius'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderBottom: isOpen ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onToggle}
          sx={{
            color: 'black',
          }}
        >
          <FilterListIcon />
        </IconButton>
        {isOpen && (
          <Typography variant="h6" sx={{ ml: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {projectDetails?.project_name}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          p: 2,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.2s',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 1, mr: -1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Site</InputLabel>
              <Select
                value={selectedSiteId}
                label="Site"
                onChange={(e) => {
                  const siteId = e.target.value as string;
                  const site = bioMapData?.sites?.find(
                    (s) => s.site_id === siteId
                  );
                  dispatch({
                    type: 'SET_BIO_INTERACTIVE_MAP_FILTER',
                    payload: {
                      selectedSite: site,
                      selectedLocation: undefined,
                      selectedDeployment: undefined,
                      selectedDevice: undefined,
                      mapOpacity: state.bioInteractiveMapFilter.mapOpacity
                    },
                  });
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {bioMapData?.sites?.map((site: Site) => (
                  <MenuItem key={site.site_id} value={site.site_id}>
                    {site.site_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filteredLocations.length > 0 && (
              <FormControl
                fullWidth
                size="small"
                disabled={selectedSiteId === 'all'}
              >
                <InputLabel>Location</InputLabel>
                <Select
                  value={selectedLocationId}
                  label="Location"
                  onChange={(e) => {
                    const locationId = e.target.value as string;
                    const location = filteredLocations.find(
                      (l) => l.location_id === locationId
                    );
                    dispatch({
                      type: 'SET_BIO_INTERACTIVE_MAP_FILTER',
                      payload: {
                        ...state.bioInteractiveMapFilter,
                        selectedLocation: location,
                        selectedDeployment: undefined,
                        selectedDevice: undefined,
                      },
                    });
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {filteredLocations.map((location: LocationAdminDetail) => (
                    <MenuItem
                      key={location.location_id}
                      value={location.location_id}
                    >
                      {location.location_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectedLocation && bioMapData?.devices && (
              <DeploymentTimeline 
                deployments={locationDeployments} 
                devices={bioMapData.devices}
                selectedDeployment={selectedDeployment}
                onSelectDeployment={handleDeploymentSelect}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ pt: 2, flexShrink: 0 }}>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, pt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={onDataPanelOpen}
              fullWidth
            >
              Files
            </Button>
            <Button
              variant="outlined"
              startIcon={<BarChartIcon />}
              onClick={onAnalyticsPanelOpen}
              fullWidth
            >
              Analysis
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default FilterPanel;